import { serviceStructureBuilder } from "../utils/helper"
import {
  FieldDefinition,
  FieldDefinitionGetter,
  FieldUniqueIdentifier,
  GetServiceFieldPath,
  IFieldRegistry,
  IRegistryGetter,
  resolvePath,
  ServiceName,
  SubmissionLayout,
} from "../utils/types"
import { COLLECTION_TO_LIST_NAME, defaultFieldRegistry } from "./mappings"

/**
 * Builds a map of FUID to list_name for fields that use value_list collections.
 * This is used by resolveListId() in submission-preview.ts as Step 3 fallback
 * for fields with sub_category = 'CSTM'.
 *
 * @param layout - The submission layout (AP_EXPORT, AP_TRANSFER, PHYTO_EXPORT)
 * @param registry - The field registry to iterate
 * @returns Map of FUID to list_name
 *
 * @see AC4 of Story #181912
 */
export const buildFuidToListNameMap = (
  layout: SubmissionLayout,
  registry: IFieldRegistry = defaultFieldRegistry,
): Map<string, string> => {
  const map = new Map<string, string>()

  for (const fieldDef of registry.values()) {
    const searchRep = fieldDef.searchRepresentation?.[layout]
    if (searchRep?.collectionName) {
      const listName = COLLECTION_TO_LIST_NAME[searchRep.collectionName]
      if (listName) {
        map.set(fieldDef.fuid, listName)
      }
    }
  }

  return map
}

// --- Factory Function ---
/**
 * Creates a specific field definition getter bound to a registry.
 */
export function makeGetFieldDefinition(
  registryGetter: IRegistryGetter,
): FieldDefinitionGetter {
  return (fuid: FieldUniqueIdentifier): FieldDefinition | undefined => {
    return registryGetter.get(fuid)
  }
}

// --- Default Instance ---
/**
 * Default field getter instance configured with the defaultFieldRegistry.
 */
export const getField: FieldDefinitionGetter =
  makeGetFieldDefinition(defaultFieldRegistry)

// getFieldPath is a convenience function to get the path of a field by its FUID
export const getServiceFieldPath: GetServiceFieldPath =
  (serverName: ServiceName, layout?: SubmissionLayout) =>
  (fuid: FieldUniqueIdentifier): string => {
    const fieldDef = getField(fuid)
    if (fieldDef) {
      return (
        resolvePath(fieldDef.serviceRepresentations[serverName], layout) ??
        "<unknown>"
      )
    }
    return "<unknown>"
  }

// --- Exports ---
export default getServiceFieldPath // Export the default instance

// Also export the factory and registry for flexibility
export * as Fuid from "./fuid" // Re-export FUID constants for easier access
export { defaultFieldRegistry } from "./mappings"

export const buildServiceStructure =
  serviceStructureBuilder(defaultFieldRegistry)
